import React, {
  ReactElement,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { EnhancedOption } from 'common/components/EnhancedOption/EnhancedOption';
import { EnhancedSelectOption, EnhancedSelectOptions } from 'common/components/EnhancedSelect/EnhancedSelect';
import { ENV_ASSET_CODE, ENV_ASSET_ISSUER } from 'common/constants/env.constants';
import { filterByValueOrLabel } from 'common/utils/form.utils';
import {
  useCounterpartyAccountDetailsQuery,
  useCounterpartyAccountsListQuery,
  useCustomerAccountsListMutation,
} from 'domains/accounts/queries/accounts.query';
import {
  TAccountDetailRes,
  TAccountEntity,
  AccountKindToFancy,
  TAccountCustomerAssetsEntity,
  TAccountCustomersResData,
  TCustomerAccountEntity,
} from 'domains/accounts/types/accounts.types';
import { formatIfIBAN } from 'domains/accounts/utils/render-account-iban';
import { useGetCounterpartyCustomersListQuery } from 'domains/customers/queries/customers.query';
import { COUNTERPARTY_TYPE_OPTIONS } from 'domains/payments/constants/payments.constants';
import { ECounterpartyType } from 'domains/payments/enums/payments.enums';
import {
  SEnhancedSelect,
  SEnhancedSelect2,
  SSelectRow,
} from './select-accounts-customers.component.style';
import { TParticipantValue } from '../../types/payments.types';

const applyAssetToOption = (
  accounts: TAccountEntity[],
  selected: string | null,
  asset: TAccountDetailRes['assets'][number] | null,
): TAccountEntity[] => {
  const result = accounts.map((account) =>
    account.wallet_account_guid === selected && asset ? { ...account, asset } : account,
  );
  return result;
};

const makeCustomerOption = (
  login: string,
  wallet_user_guid: string,
  showBalance: boolean,
  asset: TAccountCustomerAssetsEntity,
  user_guid: string,
): {
  label: string,
  value: string,
  render: () => ReactElement,
} => ({
  label: login,
  value: wallet_user_guid,
  render: (): ReactElement => (
    <EnhancedOption
      label={login}
      secondaryLabel={showBalance && user_guid === wallet_user_guid && asset ? `${asset.balance} ${asset.code}` : null}
      layout={1}
      description={wallet_user_guid}
    />
  ),
});

const makeAccountOption = ({
  wallet_account_guid,
  asset,
  account_kind,
  iban,
}: TAccountEntity & { asset?: TAccountDetailRes['assets'][number] }): {
  label: string,
  value: string,
  render: () => ReactElement,
} => ({
  value: wallet_account_guid,
  label: iban || wallet_account_guid,
  render: (): ReactElement => (
    <EnhancedOption
      label={formatIfIBAN(iban || wallet_account_guid)}
      layout={0}
      description={AccountKindToFancy[account_kind]}
      secondaryLabel={asset ? `${asset.balance} ${asset.code}` : null}
    />
  ),
});

type Props = {
  counterparty: string;
  value: string;
  typeValue?: string;
  guid?: string;
  iban?: string;
  account_guid?: string;
  onChange: (value: TParticipantValue) => void;
  size?: 'small' | 'middle' | 'large';
  mt?: number;
  showAssetInOption?: boolean;
  placeholderRecipientTransKey?: string;
  wallet_user_external_id?: string;
};

export const SelectAccountsCustomersComponent = ({
  counterparty,
  value,
  typeValue,
  guid,
  iban,
  account_guid,
  onChange,
  size = 'large',
  mt,
  showAssetInOption = true,
  wallet_user_external_id,
  placeholderRecipientTransKey = 'reporting.paymentOperations.filters.select.recipient.placeholder',
}: Props): ReactElement => {
  type TAccountData = {
    wallet_user_guid: string;
    accountGUID: string;
    asset: TAccountCustomerAssetsEntity;
    iban?: string;
  };

  const initialData = {
    wallet_user_guid: '',
    accountGUID: '',
    asset: {
      activated: false,
      balance: 0,
      code: '',
      minor_unit: 0,
      name: '',
      platform: '',
      stellar_code: '',
      stellar_issuer: '',
    },
  };

  const { t } = useTranslation();
  const [recipientAsset, setRecipientAsset] = useState<TAccountDetailRes['assets'][number] | null>(null);
  const [searchRecipientValue, setSearchRecipientValue] = useState<string>('');
  const [selectRecipientAccountsData, setSelectRecipientAccountsData] = useState<TAccountCustomersResData>();
  const [recipientAccountData, setRecipientAccountData] = useState<TAccountData>(initialData);
  const [recipientAccountValue, setRecipientAccountValue] = useState<string>('');
  const [isRecipientCustomerSelected, setIsRecipientCustomerSelected] = useState<boolean>(false);
  const [searchRecipientAccountValue, setSearchRecipientAccountValue] = useState<string>('');
  const [recipientTypeValue, setRecipientTypeValue] = useState<string>(ECounterpartyType.NotSelected);
  const [recipientValue, setRecipientValue] = useState<string>('');
  const [walletUserExternalId, setWalletUserExternalId] = useState<string>('');

  const { mutateAsync: mutateAsyncGetCustomerAccountsMutation } = useCustomerAccountsListMutation();

  const recipientCounterpartyAccountsQuery = useCounterpartyAccountsListQuery(
    counterparty,
    {},
    {
      enabled: !!counterparty,
    },
  );

  const recipientCounterpartyCustomersQuery = useGetCounterpartyCustomersListQuery(
    counterparty,
    {},
    {
      enabled: !!counterparty,
    },
  );

  const recipientType = {
    value: !typeValue ? recipientTypeValue : typeValue,
    setValue: setRecipientTypeValue,
    onChange: (val: string): void => {
      setRecipientTypeValue(val);
      setRecipientValue('');
      setSearchRecipientValue('');
      setWalletUserExternalId('');
      onChange({
        guid: '',
        type: val,
        walletUserExternalId: '',
      });
      if (recipientTypeValue === ECounterpartyType.NotSelected) {
        setRecipientValue('');
        setSearchRecipientValue('');
        setWalletUserExternalId('');
        onChange({
          guid: '',
          type: val,
          walletUserExternalId: '',
        });
      }
    },
    disabled: counterparty == null,
    options: COUNTERPARTY_TYPE_OPTIONS,
  };

  const handleRecipientSearch = (searchGUID: string): void => {
    setSearchRecipientValue(searchGUID);
  };

  const makeRecipientOptions = (): {label: string, value: string, render: () => ReactElement}[] => {
    let accounts: TAccountEntity[] = recipientCounterpartyAccountsQuery.data?.accounts || [];

    if (recipientType.value === ECounterpartyType.Bank) {
      accounts =
        recipientCounterpartyAccountsQuery.data?.accounts?.filter(
          (account) => account.account_kind !== ECounterpartyType.Customer,
        ) || [];
    }

    let targetOptions = applyAssetToOption(accounts || [], value, recipientAsset).map(makeAccountOption);

    if (recipientType.value === ECounterpartyType.Customer) {
      targetOptions = (recipientCounterpartyCustomersQuery?.data?.customers || [])?.map(({ login, wallet_user_guid }) =>
        makeCustomerOption(
          login,
          wallet_user_guid,
          false,
          recipientAccountData?.asset,
          recipientAccountData?.wallet_user_guid,
        ),
      );
    }

    return targetOptions;
  };

  const filterAsset = (accounts: TCustomerAccountEntity[]): TCustomerAccountEntity[] =>
    accounts?.filter((accData) =>
      accData?.assets?.find(
        (asset) => asset?.stellar_issuer === ENV_ASSET_ISSUER && asset?.stellar_code === ENV_ASSET_CODE,
      ),
    );

  const recipient = {
    value: recipientValue,
    onChange: async (val: string): Promise<void> => {
      onChange({
        guid: val,
        type: !typeValue ? recipientType.value : typeValue,
        walletUserExternalId: recipient.options?.filter(item => item.value === val)[0]?.label,
      });
      setRecipientValue(!guid ? val : guid);
      setSearchRecipientValue('');
      setRecipientAccountValue('');
      setIsRecipientCustomerSelected(false);
      setWalletUserExternalId(recipient.options?.filter(item => item.value === val)[0]?.label);

      if (recipientType.value === ECounterpartyType.Customer && val) {
        if (counterparty) {
          const customerAccountsList = await mutateAsyncGetCustomerAccountsMutation({
            counterpartyGUID: counterparty,
            customerGUID: val,
          });

          if (customerAccountsList) {
            const customersAcc = filterAsset(customerAccountsList.accounts);

            if (customersAcc) {
              setSelectRecipientAccountsData({ accounts: customersAcc, total: customerAccountsList?.total });
            }

            if (customerAccountsList?.total > 1) {
              setIsRecipientCustomerSelected(true);
            } else {
              setIsRecipientCustomerSelected(false);
              setRecipientAccountValue(customerAccountsList?.accounts?.[0]?.guid);

              if (val && customersAcc?.length > 0) {
                onChange({
                  guid: customerAccountsList?.accounts[0]?.guid,
                  type: recipientType.value,
                  walletUserExternalId: recipient.options?.filter(item => item.value === val)[0]?.label,
                });
                setRecipientValue(customerAccountsList?.accounts[0]?.guid);
                setRecipientAccountData({
                  wallet_user_guid: val,
                  accountGUID: customerAccountsList?.accounts[0]?.guid,
                  asset: { ...customersAcc[0]?.assets[0] },
                  iban: customerAccountsList?.accounts[0]?.iban,
                });
              }
            }
          }
        }
      }
    },
    disabled: recipientType.value === ECounterpartyType.NotSelected,
    options: makeRecipientOptions(),
    onClear: (): void => {
      setWalletUserExternalId('');
      setRecipientValue('');
    },
  };

  const recipientDetailAccounts = {
    value: recipientAccountValue,
    setValue: setRecipientAccountValue,
    onChange: (recipientDetailAccountValue: string): void => {
      setRecipientAccountValue(recipientDetailAccountValue);
      onChange({
        guid: recipientDetailAccountValue,
        type: recipientType.value,
        walletUserExternalId: wallet_user_external_id || walletUserExternalId,
      });
      setRecipientValue(recipientDetailAccountValue);

      const data: TCustomerAccountEntity[] = selectRecipientAccountsData
        ? selectRecipientAccountsData?.accounts?.filter((account) => account.guid === recipientDetailAccountValue)
        : [];

      setRecipientAccountData({
        wallet_user_guid: value || '',
        accountGUID: data[0]?.guid,
        asset: { ...data[0]?.assets[0] },
        iban: data[0]?.iban,
      });
    },
    disabled: false,
    onClear: (): void => {
      setRecipientAccountValue('');
      setSearchRecipientValue('');
    },
    options: selectRecipientAccountsData?.total
      ? selectRecipientAccountsData?.accounts?.map((account) => ({
          label: formatIfIBAN(account.iban || account.guid),
          value: account.guid,
        }))
      : [],
  };

  const handleRecipientAccountSearch = (searchValue: string): void => {
    setSearchRecipientAccountValue(searchValue);
  };

  const filterWithSecondaryLabel = (searchValue: string, options: any): EnhancedSelectOptions => {
    let filteredOptions: EnhancedSelectOptions = [];

    if (searchValue?.length > 0) {
      filteredOptions = filterByValueOrLabel(options, searchValue).map((option: EnhancedSelectOption) => ({
        ...option,
        secondaryLabel: '',
      }));
    } else {
      filteredOptions = options;
    }

    return filteredOptions;
  };

  const filterFn = (searchValue: string, options: any): EnhancedSelectOptions => {
    let filteredOpt: EnhancedSelectOptions = [];

    if (searchValue?.length > 0) {
      filterByValueOrLabel(options, searchValue);
    } else {
      filteredOpt = options;
    }

    return filteredOpt;
  };

  const filteredOptions = filterWithSecondaryLabel(searchRecipientValue, recipient.options);

  const filteredRecipientAccountOptions = filterFn(searchRecipientAccountValue, recipientDetailAccounts.options);

  const {
    data: useCounterpartyAccountDetailsData,
  } = useCounterpartyAccountDetailsQuery(
    counterparty!,
    value!,
    {},
    {
      enabled: !!value && showAssetInOption && recipientType.value === ECounterpartyType.Bank,
    },
  );

  useEffect(() => {
    if (useCounterpartyAccountDetailsData) {
      const recipientAccAsset = useCounterpartyAccountDetailsData?.assets.find(
        (asset: any) => asset.stellar_issuer === ENV_ASSET_ISSUER && asset.stellar_code === ENV_ASSET_CODE,
      )!;
      setRecipientAsset(recipientAccAsset);
    }
  }, [useCounterpartyAccountDetailsData]);

  useEffect(() => {
    setRecipientValue('');
    setSearchRecipientValue('');
    setWalletUserExternalId('');
  },[counterparty]);

  useEffect(() => {
    if (guid) recipient.onChange(guid);
    if (account_guid) recipientDetailAccounts.onChange(account_guid);
  }, [account_guid, iban]);

  return (
    <div>
      <SSelectRow mt={mt}>
        <SEnhancedSelect
          style={{ width: '140px' }}
          size={size}
          value={recipientType.value}
          disabled={recipientType.disabled}
          placeholder={t('reporting.paymentOperations.filters.select.counterpartyType.placeholder')}
          onChange={recipientType.onChange}
          options={recipientType.options}
          rootClassName="select-accounts-customers"
        />
        <SEnhancedSelect
          style={{ width: '300px' }}
          size={size}
          allowClear
          value={
            (recipientType.value === ECounterpartyType.Customer)
                  ? (wallet_user_external_id || walletUserExternalId)
                  : (guid || recipientValue)
          }
          placeholder={t(placeholderRecipientTransKey)}
          onChange={recipient.onChange}
          disabled={recipient.disabled}
          options={filteredOptions}
          showSearch
          filterOption={false}
          onSearch={handleRecipientSearch}
          onClear={recipient.onClear}
        />
        {recipientType.value === ECounterpartyType.Customer && isRecipientCustomerSelected && guid && (
          <SEnhancedSelect2
            style={{ width: '340px' }}
            size={size}
            allowClear
            onClear={recipientDetailAccounts.onClear}
            value={recipientDetailAccounts.value}
            disabled={recipientDetailAccounts.disabled}
            placeholder={t('reporting.paymentOperations.filters.select.account.placeholder')}
            onChange={recipientDetailAccounts.onChange}
            options={filteredRecipientAccountOptions}
            showSearch
            filterOption={false}
            onSearch={handleRecipientAccountSearch}
          />
        )}
      </SSelectRow>
    </div>
  );
};
